import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import {
  Bell,
  Mail,
  Smartphone,
  MessageSquare,
  Monitor,
  DollarSign,
  Moon,
  Shield,
  Megaphone,
  Settings,
  AlertCircle,
  Check,
} from 'lucide-react';

interface NotificationPreferences {
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  inAppEnabled: boolean;
  transactionAlerts: boolean;
  securityAlerts: boolean;
  marketingEmails: boolean;
  systemNotifications: boolean;
  transactionThreshold: number;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
}

export function NotificationSettings() {
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<NotificationPreferences | null>(null);

  // Fetch notification preferences
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['banking', 'settings', 'notifications'],
    queryFn: async () => {
      const response = await api.get('/banking/settings/notifications/preferences');
      return response.data as NotificationPreferences;
    },
  });

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<NotificationPreferences>) => {
      const response = await api.put('/banking/settings/notifications/preferences', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'settings', 'notifications'] });
      setHasChanges(false);
    },
  });

  // Initialize local state when data loads
  useEffect(() => {
    if (preferences && !localPrefs) {
      setLocalPrefs(preferences);
    }
  }, [preferences, localPrefs]);

  const handleToggle = (key: keyof NotificationPreferences) => {
    if (!localPrefs) return;
    
    // Security alerts cannot be disabled
    if (key === 'securityAlerts') return;
    
    setLocalPrefs({ ...localPrefs, [key]: !localPrefs[key] });
    setHasChanges(true);
  };

  const handleThresholdChange = (value: string) => {
    if (!localPrefs) return;
    const numValue = parseInt(value.replace(/\D/g, ''), 10) || 0;
    setLocalPrefs({ ...localPrefs, transactionThreshold: numValue });
    setHasChanges(true);
  };

  const handleTimeChange = (field: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
    if (!localPrefs) return;
    setLocalPrefs({ ...localPrefs, [field]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!localPrefs) return;
    updateMutation.mutate(localPrefs);
  };

  const handleReset = () => {
    if (preferences) {
      setLocalPrefs(preferences);
      setHasChanges(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  if (isLoading || !localPrefs) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Preferências de Notificação</h1>
          <p className="text-surface-400">Carregando...</p>
        </div>
        <Card>
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-surface-800/50 rounded-lg animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Preferências de Notificação</h1>
          <p className="text-surface-400">Configure como deseja receber notificações</p>
        </div>
        {hasChanges && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleReset}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        )}
      </div>

      {/* Canais de Notificação */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Bell size={24} className="text-primary-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Canais de Notificação</h2>
              <p className="text-sm text-surface-400">Escolha como deseja receber suas notificações</p>
            </div>
          </div>

          <div className="space-y-3">
            <ToggleItem
              icon={<Mail size={20} />}
              title="E-mail"
              description="Receba notificações por e-mail"
              checked={localPrefs.emailEnabled}
              onChange={() => handleToggle('emailEnabled')}
            />

            <ToggleItem
              icon={<Smartphone size={20} />}
              title="Push"
              description="Notificações push no navegador"
              checked={localPrefs.pushEnabled}
              onChange={() => handleToggle('pushEnabled')}
            />

            <ToggleItem
              icon={<MessageSquare size={20} />}
              title="SMS"
              description="Receba alertas por SMS"
              checked={localPrefs.smsEnabled}
              onChange={() => handleToggle('smsEnabled')}
            />

            <ToggleItem
              icon={<Monitor size={20} />}
              title="In-App"
              description="Notificações dentro do aplicativo"
              checked={localPrefs.inAppEnabled}
              onChange={() => handleToggle('inAppEnabled')}
            />
          </div>
        </div>
      </Card>

      {/* Tipos de Notificação */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Settings size={24} className="text-primary-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Tipos de Notificação</h2>
              <p className="text-sm text-surface-400">Escolha quais tipos de notificação deseja receber</p>
            </div>
          </div>

          <div className="space-y-3">
            <ToggleItem
              icon={<DollarSign size={20} />}
              title="Alertas de Transação"
              description="Notificações sobre movimentações financeiras"
              checked={localPrefs.transactionAlerts}
              onChange={() => handleToggle('transactionAlerts')}
            />

            <ToggleItem
              icon={<Shield size={20} />}
              title="Alertas de Segurança"
              description="Notificações sobre atividades de segurança"
              checked={localPrefs.securityAlerts}
              onChange={() => {}}
              disabled
              badge="Obrigatório"
            />

            <ToggleItem
              icon={<Megaphone size={20} />}
              title="E-mails de Marketing"
              description="Ofertas, promoções e novidades"
              checked={localPrefs.marketingEmails}
              onChange={() => handleToggle('marketingEmails')}
            />

            <ToggleItem
              icon={<AlertCircle size={20} />}
              title="Notificações do Sistema"
              description="Atualizações e manutenções programadas"
              checked={localPrefs.systemNotifications}
              onChange={() => handleToggle('systemNotifications')}
            />
          </div>
        </div>
      </Card>

      {/* Limite de Transação */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign size={24} className="text-primary-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Limite de Notificação</h2>
              <p className="text-sm text-surface-400">
                Receba notificações apenas para transações acima deste valor
              </p>
            </div>
          </div>

          <div className="max-w-md">
            <Input
              label="Valor mínimo para notificação"
              value={formatCurrency(localPrefs.transactionThreshold)}
              onChange={(e) => handleThresholdChange(e.target.value)}
              placeholder="R$ 0,00"
            />
            <p className="text-xs text-surface-400 mt-2">
              {localPrefs.transactionThreshold === 0 
                ? 'Você receberá notificações para todas as transações'
                : `Você receberá notificações apenas para transações acima de ${formatCurrency(localPrefs.transactionThreshold)}`
              }
            </p>
          </div>
        </div>
      </Card>

      {/* Horário de Silêncio */}
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Moon size={24} className="text-primary-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Horário de Silêncio</h2>
              <p className="text-sm text-surface-400">
                Pause notificações durante determinados horários (exceto segurança)
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <ToggleItem
              icon={<Moon size={20} />}
              title="Ativar Horário de Silêncio"
              description="Pausar notificações não urgentes durante o período definido"
              checked={localPrefs.quietHoursEnabled}
              onChange={() => handleToggle('quietHoursEnabled')}
            />

            {localPrefs.quietHoursEnabled && (
              <div className="grid grid-cols-2 gap-4 mt-4 pl-12">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Início
                  </label>
                  <input
                    type="time"
                    value={localPrefs.quietHoursStart || '22:00'}
                    onChange={(e) => handleTimeChange('quietHoursStart', e.target.value)}
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Fim
                  </label>
                  <input
                    type="time"
                    value={localPrefs.quietHoursEnd || '07:00'}
                    onChange={(e) => handleTimeChange('quietHoursEnd', e.target.value)}
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}

            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mt-4">
              <div className="flex items-start gap-2">
                <Shield size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-300/80">
                  Alertas de segurança sempre serão entregues, independente do horário de silêncio.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Success/Error Messages */}
      {updateMutation.isSuccess && (
        <div className="fixed bottom-4 right-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-2">
          <Check size={20} className="text-green-400" />
          <span className="text-green-300">Preferências salvas com sucesso!</span>
        </div>
      )}

      {updateMutation.isError && (
        <div className="fixed bottom-4 right-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-400" />
          <span className="text-red-300">Erro ao salvar preferências. Tente novamente.</span>
        </div>
      )}
    </div>
  );
}

// Toggle Item Component
interface ToggleItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  badge?: string;
}

function ToggleItem({ icon, title, description, checked, onChange, disabled, badge }: ToggleItemProps) {
  const handleClick = () => {
    if (!disabled) {
      onChange();
    }
  };

  return (
    <div 
      onClick={handleClick}
      className={`flex items-center justify-between p-4 rounded-lg bg-surface-800/50 ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-800'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400">
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-white">{title}</p>
            {badge && (
              <span className="px-2 py-0.5 text-xs bg-primary-500/20 text-primary-300 rounded-full">
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-surface-400">{description}</p>
        </div>
      </div>
      <div 
        className="relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={handleClick}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent 
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-surface-900
            ${checked ? 'bg-primary-500' : 'bg-surface-600'}
            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
              transition duration-200 ease-in-out
              ${checked ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
      </div>
    </div>
  );
}

export default NotificationSettings;
